import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import WizardShell from "./WizardShell";
import { generateEvolve } from "@/lib/api";

export default function EvolveStep({
  attention,
  witness,
  contextText,
  refinedProblem,
  kataHistory,
  onPlanReady,
  onBack,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const didGenerateRef = useRef(false);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const plan = await generateEvolve({
        refined_problem: refinedProblem,
        attention,
        witness,
        context: contextText || undefined,
        kata_history: kataHistory,
      });
      onPlanReady(plan);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Something went wrong.";
      setError(msg);
      toast.error("Couldn't write your plan", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didGenerateRef.current) return;
    didGenerateRef.current = true;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WizardShell
      pillLabel="Step 5 · Evolve"
      pillColor="#4aa0a4"
      headline="Writing your 90-day plan."
      aiOpening={
        "Pulling together everything you've shared — the attention, the witness, the sparring — into a plan you can actually act on."
      }
      testid="evolve-step"
      footer={
        error ? (
          <>
            <button
              type="button"
              className="weave-btn-outline"
              onClick={onBack}
              data-testid="evolve-back-btn"
            >
              ← Back
            </button>
            <button
              type="button"
              className="weave-btn-primary"
              onClick={generate}
              data-testid="evolve-retry-btn"
            >
              Try again
            </button>
          </>
        ) : null
      }
    >
      {loading && (
        <div className="flex items-center gap-3 text-slate-500" data-testid="evolve-loading">
          <span className="weave-spinner" />
          Drafting 30-, 60-, and 90-day commitments…
        </div>
      )}
      {error && !loading && (
        <div className="weave-card-soft" data-testid="evolve-error">
          <p className="text-slate-700">
            I couldn't finish writing your plan just now.
          </p>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      )}
    </WizardShell>
  );
}
